import React, { useEffect, useId, useState } from 'react';
import { Task, TaskComment } from '../types/Task';
import { formatTaskDate, formatTaskDateTime, isTaskDueDateOverdue } from '../utils/taskDueDate';
import './LoginModal.css';

interface TaskDetailModalProps {
  isOpen: boolean;
  task: Task | null;
  loading?: boolean;
  error?: string | null;
  onAddComment?: (text: string) => Promise<void>;
  onUpdateComment?: (commentId: number, text: string) => Promise<void>;
  onDeleteComment?: (commentId: number) => Promise<void>;
  onClose: () => void;
  presentation?: 'modal' | 'standalone';
}

const MAX_COMMENT_LENGTH = 500;

const formatLabel = (value: string | undefined) => {
  if (!value) return 'Not set';
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatLabels = (labels: string[] | undefined) => {
  if (!labels || labels.length === 0) return 'None';
  return labels.join(', ');
};

const normalizeCommentText = (value: string) => value.trim();

const getCommentTimestamp = (comment: TaskComment) => comment.updatedAt || comment.createdAt;

const formatAttachmentSize = (fileSizeBytes: number) => {
  if (fileSizeBytes >= 1024 * 1024) {
    const sizeInMegabytes = fileSizeBytes / (1024 * 1024);
    return `${Number.isInteger(sizeInMegabytes) ? sizeInMegabytes : sizeInMegabytes.toFixed(1)} MB`;
  }

  if (fileSizeBytes >= 1024) {
    return `${Math.round(fileSizeBytes / 1024)} KB`;
  }

  return `${fileSizeBytes} B`;
};

function TaskDetailSkeleton() {
  return (
    <div
      className="task-detail-skeleton"
      role="status"
      aria-live="polite"
      aria-label="Loading task details"
      data-testid="task-detail-skeleton"
    >
      <div className="task-detail-skeleton-section" aria-hidden="true">
        <span className="task-skeleton-block task-skeleton-block--eyebrow" />
        <span className="task-skeleton-block task-skeleton-block--detail-title" />
        <span className="task-skeleton-block task-skeleton-block--detail-line" />
        <span className="task-skeleton-block task-skeleton-block--detail-line task-skeleton-block--detail-line-short" />
      </div>

      <div className="task-detail-skeleton-grid" aria-hidden="true">
        {Array.from({ length: 6 }, (_, index) => (
          <div key={index} className="task-detail-skeleton-item">
            <span className="task-skeleton-block task-skeleton-block--label" />
            <span className="task-skeleton-block task-skeleton-block--value" />
          </div>
        ))}
      </div>

      <div className="task-detail-skeleton-section" aria-hidden="true">
        <div className="task-detail-skeleton-heading">
          <span className="task-skeleton-block task-skeleton-block--eyebrow" />
          <span className="task-skeleton-block task-skeleton-block--section-title" />
        </div>
        <span className="task-skeleton-block task-skeleton-block--detail-line" />
        <span className="task-skeleton-block task-skeleton-block--detail-line" />
      </div>

      <div className="task-detail-skeleton-section" aria-hidden="true">
        <div className="task-detail-skeleton-heading">
          <span className="task-skeleton-block task-skeleton-block--eyebrow" />
          <span className="task-skeleton-block task-skeleton-block--section-title" />
        </div>
        <span className="task-skeleton-block task-skeleton-block--detail-line" />
        <span className="task-skeleton-block task-skeleton-block--detail-line task-skeleton-block--detail-line-short" />
      </div>
    </div>
  );
}

export const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  isOpen,
  task,
  loading = false,
  error = null,
  onAddComment,
  onUpdateComment,
  onDeleteComment,
  onClose,
  presentation = 'modal',
}) => {
  const titleId = useId();
  const [newCommentText, setNewCommentText] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submitting) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, submitting]);

  useEffect(() => {
    if (!isOpen) {
      setNewCommentText('');
      setEditingCommentId(null);
      setEditingText('');
      setCommentError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleMutationError = (mutationError: unknown, fallbackMessage: string) => {
    if (mutationError instanceof Error && mutationError.message) {
      setCommentError(mutationError.message);
      return;
    }
    setCommentError(fallbackMessage);
  };

  const handleAddComment = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedText = normalizeCommentText(newCommentText);

    if (!normalizedText) {
      setCommentError('Comment text is required.');
      return;
    }

    if (normalizedText.length > MAX_COMMENT_LENGTH) {
      setCommentError(`Comment must be ${MAX_COMMENT_LENGTH} characters or fewer.`);
      return;
    }

    if (!onAddComment) {
      return;
    }

    setSubmitting(true);
    setCommentError(null);

    try {
      await onAddComment(normalizedText);
      setNewCommentText('');
    } catch (mutationError) {
      handleMutationError(mutationError, 'Failed to add comment.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveComment = async (commentId: number) => {
    const normalizedText = normalizeCommentText(editingText);

    if (!normalizedText) {
      setCommentError('Comment text is required.');
      return;
    }

    if (normalizedText.length > MAX_COMMENT_LENGTH) {
      setCommentError(`Comment must be ${MAX_COMMENT_LENGTH} characters or fewer.`);
      return;
    }

    if (!onUpdateComment) {
      return;
    }

    setSubmitting(true);
    setCommentError(null);

    try {
      await onUpdateComment(commentId, normalizedText);
      setEditingCommentId(null);
      setEditingText('');
    } catch (mutationError) {
      handleMutationError(mutationError, 'Failed to update comment.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId: number) => {
    if (!onDeleteComment) {
      return;
    }

    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    setSubmitting(true);
    setCommentError(null);

    try {
      await onDeleteComment(commentId);
      if (editingCommentId === commentId) {
        setEditingCommentId(null);
        setEditingText('');
      }
    } catch (mutationError) {
      handleMutationError(mutationError, 'Failed to delete comment.');
    } finally {
      setSubmitting(false);
    }
  };

  const comments = task?.comments ?? [];
  const checklistItems = task?.checklistItems ?? [];
  const attachments = task?.attachments ?? [];
  const completedChecklistItems = checklistItems.filter((item) => item.done).length;
  const modalTask = task;
  const isStandalone = presentation === 'standalone';

  const detailContent = (
    <>
      <div className="modal-header">
        <h2 id={titleId}>Task Details</h2>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close task details" disabled={submitting}>
          ×
        </button>
      </div>

      <div className="task-detail-body">
        {loading ? (
          <TaskDetailSkeleton />
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : modalTask ? (
          <>
            <section className="task-detail-section">
              <p className="task-detail-eyebrow">Overview</p>
              <h3 className="task-detail-title">{modalTask.title}</h3>
              <p className="task-detail-description">{modalTask.description || 'No description provided.'}</p>
            </section>

            <dl className="task-detail-grid">
              <div className="task-detail-item">
                <dt>Status</dt>
                <dd>{formatLabel(modalTask.status)}</dd>
              </div>
              <div className="task-detail-item">
                <dt>Priority</dt>
                <dd>{formatLabel(modalTask.priority)}</dd>
              </div>
              <div className="task-detail-item">
                <dt>Labels</dt>
                <dd>{formatLabels(modalTask.labels)}</dd>
              </div>
              <div className="task-detail-item">
                <dt>Due date</dt>
                <dd>
                  <span className={isTaskDueDateOverdue(modalTask.dueDate) ? 'task-date-value is-overdue' : 'task-date-value'}>
                    {formatTaskDate(modalTask.dueDate, 'Not set')}
                    {isTaskDueDateOverdue(modalTask.dueDate) && <span className="task-overdue-badge">Overdue</span>}
                  </span>
                </dd>
              </div>
              <div className="task-detail-item">
                <dt>Task ID</dt>
                <dd>{modalTask.id ?? 'Not set'}</dd>
              </div>
              <div className="task-detail-item">
                <dt>Created</dt>
                <dd>{formatTaskDateTime(modalTask.createdAt, 'Not set')}</dd>
              </div>
              <div className="task-detail-item">
                <dt>Updated</dt>
                <dd>{formatTaskDateTime(modalTask.updatedAt, 'Not set')}</dd>
              </div>
            </dl>

            <section className="task-detail-section">
              <div className="task-comments-header">
                <div>
                  <p className="task-detail-eyebrow">Checklist</p>
                  <h3 className="task-comments-title">Subtasks</h3>
                </div>
                <span
                  className="task-comments-count"
                  aria-label={`${completedChecklistItems} of ${checklistItems.length} checklist items done`}
                >
                  {completedChecklistItems}/{checklistItems.length}
                </span>
              </div>

              {checklistItems.length === 0 ? (
                <p className="task-comments-status">No checklist items.</p>
              ) : (
                <ol className="task-checklist-display-list">
                  {checklistItems.map((item, index) => (
                    <li
                      key={item.id ?? `${item.text}-${index}`}
                      className={item.done ? 'task-checklist-display-item is-done' : 'task-checklist-display-item'}
                    >
                      <span className="task-checklist-display-status">{item.done ? 'Done' : 'Open'}</span>
                      <span className="task-checklist-display-text">{item.text}</span>
                    </li>
                  ))}
                </ol>
              )}
            </section>

            <section className="task-detail-section task-comments-section">
              <div className="task-comments-header">
                <div>
                  <p className="task-detail-eyebrow">Attachments</p>
                  <h3 className="task-comments-title">Stored files</h3>
                </div>
                <span className="task-comments-count" aria-label={`${attachments.length} attachments`}>
                  {attachments.length}
                </span>
              </div>

              {attachments.length === 0 ? (
                <p className="task-comments-status">No attachments.</p>
              ) : (
                <ul className="task-attachment-list">
                  {attachments.map((attachment) => (
                    <li key={attachment.id} className="task-attachment-item">
                      <div className="task-attachment-main">
                        <span className="task-attachment-name">{attachment.fileName}</span>
                        <span className="task-attachment-meta">
                          {formatAttachmentSize(attachment.fileSizeBytes)}
                          {attachment.contentType ? ` · ${attachment.contentType}` : ''}
                          {attachment.uploadedAt ? ` · Added ${formatTaskDateTime(attachment.uploadedAt, 'Not set')}` : ''}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="task-detail-section task-comments-section">
              <div className="task-comments-header">
                <div>
                  <p className="task-detail-eyebrow">Comments</p>
                  <h3 className="task-comments-title">Notes and activity</h3>
                </div>
                <span className="task-comments-count" aria-label={`${comments.length} comments`}>
                  {comments.length}
                </span>
              </div>

              {commentError && <div className="error-message">{commentError}</div>}
              {submitting && (
                <div className="task-comment-progress" role="status" data-testid="task-comment-progress">
                  <span className="task-inline-spinner" aria-hidden="true" />
                  <span>Updating comments...</span>
                </div>
              )}

              <form className="task-comment-form" onSubmit={handleAddComment}>
                <label className="task-comment-label" htmlFor={`${titleId}-new-comment`}>
                  New comment
                </label>
                <textarea
                  id={`${titleId}-new-comment`}
                  className="task-comment-textarea"
                  value={newCommentText}
                  onChange={(event) => setNewCommentText(event.target.value)}
                  rows={3}
                  maxLength={MAX_COMMENT_LENGTH}
                  placeholder="Add a comment"
                  disabled={submitting || loading}
                />
                <div className="task-comment-form-footer">
                  <span className="task-comment-help">{newCommentText.length}/{MAX_COMMENT_LENGTH}</span>
                  <button type="submit" className="btn-primary" disabled={submitting || loading}>
                    Add comment
                  </button>
                </div>
              </form>

              {comments.length === 0 ? (
                <p className="task-comments-status">No comments yet.</p>
              ) : (
                <ol className="task-comment-list">
                  {comments.map((comment) => {
                    const isEditing = editingCommentId === comment.id;

                    return (
                      <li key={comment.id} className="task-comment-item">
                        <div className="task-comment-meta">
                          <span className="task-comment-timestamp">{formatTaskDateTime(getCommentTimestamp(comment), 'Not set')}</span>
                          <div className="task-comment-actions">
                            {isEditing ? (
                              <>
                                <button
                                  type="button"
                                  className="btn-primary"
                                  onClick={() => void handleSaveComment(comment.id)}
                                  disabled={submitting}
                                >
                                  Save comment changes
                                </button>
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={() => {
                                    setEditingCommentId(null);
                                    setEditingText('');
                                    setCommentError(null);
                                  }}
                                  disabled={submitting}
                                >
                                  Cancel comment editing
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={() => {
                                    setEditingCommentId(comment.id);
                                    setEditingText(comment.text);
                                    setCommentError(null);
                                  }}
                                  disabled={submitting}
                                >
                                  Edit comment
                                </button>
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={() => void handleDelete(comment.id)}
                                  disabled={submitting}
                                >
                                  Delete comment
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {isEditing ? (
                          <div className="task-comment-editing">
                            <label className="task-comment-label" htmlFor={`${titleId}-edit-comment-${comment.id}`}>
                              Edit comment
                            </label>
                            <textarea
                              id={`${titleId}-edit-comment-${comment.id}`}
                              className="task-comment-textarea"
                              value={editingText}
                              onChange={(event) => setEditingText(event.target.value)}
                              rows={3}
                              maxLength={MAX_COMMENT_LENGTH}
                              disabled={submitting}
                            />
                          </div>
                        ) : (
                          <p className="task-comment-text">{comment.text}</p>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>
          </>
        ) : (
          <p className="task-comments-status">Task details are unavailable.</p>
        )}
      </div>

      <div className="modal-actions">
        <button type="button" onClick={onClose} className="btn-secondary" disabled={submitting}>
          Close
        </button>
      </div>
    </>
  );

  if (isStandalone) {
    return (
      <section className="task-detail-page-shell" aria-labelledby={titleId}>
        <article className="modal-content task-detail-modal task-detail-page-card">
          {detailContent}
        </article>
      </section>
    );
  }

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget && !submitting) {
          onClose();
        }
      }}
    >
      <div className="modal-content task-detail-modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        {detailContent}
      </div>
    </div>
  );
};

export default TaskDetailModal;